import type { Line, Polygon } from '../../geometry/types';

export type RatiolePuzzle = {
  templateId?: string;
  familyId?: string;
  polygon: Polygon;
  targetPercent: number;
  solutionCut: Line;
  achievableError: number;
  quality?: {
    area: number;
    occupancy: number;
    minimumFeatureWidth: number;
    holeClearance: number;
    intersectionCount: number;
    vertexClearance: number;
    solutionWidth: number;
    achievedPercent: number;
    concave: boolean;
    hasHole: boolean;
  };
};

function isPoint(value: unknown): value is { x: number; y: number } {
  if (!value || typeof value !== 'object') return false;
  const point = value as { x?: unknown; y?: unknown };
  return (
    typeof point.x === 'number' &&
    Number.isFinite(point.x) &&
    typeof point.y === 'number' &&
    Number.isFinite(point.y)
  );
}

export function isRatiolePuzzle(value: unknown): value is RatiolePuzzle {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<RatiolePuzzle>;
  return (
    (typeof candidate.templateId === 'string' || typeof candidate.familyId === 'string') &&
    Boolean(candidate.polygon) &&
    Array.isArray(candidate.polygon?.outer) &&
    candidate.polygon.outer.length >= 3 &&
    candidate.polygon.outer.every(isPoint) &&
    Array.isArray(candidate.polygon.holes) &&
    candidate.polygon.holes.every(
      (hole) => Array.isArray(hole) && hole.length >= 3 && hole.every(isPoint),
    ) &&
    typeof candidate.targetPercent === 'number' &&
    candidate.targetPercent > 0 &&
    candidate.targetPercent <= 50 &&
    Boolean(candidate.solutionCut) &&
    isPoint(candidate.solutionCut?.start) &&
    isPoint(candidate.solutionCut?.end) &&
    typeof candidate.achievableError === 'number' &&
    candidate.achievableError >= 0 &&
    candidate.achievableError <= (candidate.familyId ? 0.1 : 2)
  );
}
